/*
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */

import React, {useEffect, useRef, useState} from "react";
import {service as UnitService} from "../../../Services/UnitService";
import {FormControl, InputGroup} from "react-bootstrap";

export function UnitNumberFieldEditor(props) {

	const inputElement = useRef();
	const {Value: value, FieldDef: fieldDef = {}} = props;
	const {customData = {}} = fieldDef;
	const {unitId = props?.unitId ?? -1} = customData;

	const unit = UnitService.CachedValueById(unitId)?.result;
	const unsignedUnits = ['acre'];
	const canBeNegative = !unsignedUnits.includes(unit?.name);

	const format = (number, canBeNegative) => {
		if (number === "") {
			return number;
		}

		// this prevents the default formatter from stripping 0
		// characters from the fraction as the user is typing
		const formatterOptions = {
			maximumFractionDigits: 6,
			minimumFractionDigits: Math.min((number?.split?.('.')?.[1]?.length) ?? 0, 6),
			maximumSignificantDigits: 21
		};

		const {customFormatter} = props?.FieldDef?.customData ?? {};
		const formatter = customFormatter ? customFormatter : Intl.NumberFormat(undefined, formatterOptions);

		if (!canBeNegative && number < 0) {
			number = Math.abs(number);
		}

		let formattedNumber = number != null ? formatter.format(number) : "";

		// this prevents the default formatter from wiping
		// out the input if it doesn't recognize a number
		if (formattedNumber == "NaN") {
			return number;
		}

		// this prevents the default formatter from stripping .
		// characters from the fraction as the user is typing
		if (typeof number == 'string' && number.endsWith('.')) {
			formattedNumber += '.';
		}

		return formattedNumber;
	}

	const [formattedValue, setFormattedValue] = useState(format(value, canBeNegative));
	const [previousSelectionStart, setPreviousSelectionStart] = useState(null)
	const [numberOfCommasBefore, setNumberOfCommasBefore] = useState((formattedValue ?? "").split(',').length)

	useEffect(() => {
		setFormattedValue(format(value, canBeNegative));
	}, [value, canBeNegative])

	// useEffect(() => {
	//     if (!setSelectionStart) return;
	//     inputElement.current?.focus();
	//     inputElement.current?.setSelectionRange(selectionStart, selectionStart);
	//     setSelectionStart(null);
	// }, [selectionStart])

	useEffect(() => {
		if (previousSelectionStart !== null) {
			const numberOfCommasAfter = formattedValue.split(',').length;
			const selectionStart = previousSelectionStart + (numberOfCommasAfter - numberOfCommasBefore); // adjust the cursor position if we added any commas
			inputElement.current?.setSelectionRange(selectionStart, selectionStart); // set the cursor position on setState callback handler
		}
	}, [formattedValue])

	const onChange = (event) => {
		const targetElement = event.target;
		// if the selection is at the end, don't change it, but otherwise record the cursor position
		setPreviousSelectionStart(targetElement.value.length === targetElement.selectionEnd ? null : targetElement.selectionStart);
		setNumberOfCommasBefore(targetElement.value.split(',').length);

		const cleanValue = targetElement.value.replace(/\,/g, '');

		setFormattedValue(format(cleanValue, canBeNegative));

		props.OnUpdate?.(cleanValue);
	}

	const isPrefix = unit?.isPrefix || false;
	const unitLabel = unit?.abbreviation || "";

	const formControl = <FormControl
		type="text"
		value={formattedValue}
		onChange={onChange}
		ref={inputElement}
	/>;

	// omit InputGroup to keep rounded corners when there is no label
	if (!unitLabel) {
		return formControl;
	}

	return <InputGroup>
		{!!isPrefix && <InputGroup.Append><InputGroup.Text>{unitLabel}</InputGroup.Text></InputGroup.Append>}
		{formControl}
		{!isPrefix && <InputGroup.Append><InputGroup.Text>{unitLabel}</InputGroup.Text></InputGroup.Append>}
	</InputGroup>;
}